<template>
  <div>
    <div
      @click="goBack"
      class="cursor-pointer flex items-center my-5 justify-start border rounded-lg w-20 px-3 py-2"
    >
      <back-arrow />
      <p class="text-root-4 ml-2 font-normal text-xs">Back</p>
    </div>
    <div class="w-full bg-root border rounded-lg px-5 lg:px-8 py-8">
      <div>
        <div class="flex justify-between items-center mb-5">
          <p class="text-xs lg:text-sm font-medium">Transfer Details</p>
          <p
            @click="edit"
            class="cursor-pointer text-primary font-normal underline text-sm"
          >
            Edit
          </p>
        </div>
        <div class="flex justify-between items-center mb-3">
          <p class="text-xs lg:text-sm font-thin">
            {{ hasBeenDebited ? 'You have sent exactly' : 'You send exactly' }}
          </p>
          <p class="text-base font-bold">
            {{
              fromBalance
                ? parseMoney(transferDetails.debitAmount)
                : parseMoneyAll(
                    transferDetails.debitAmount +
                      transferDetails.partnerCommission,
                    country.currency
                  ) || 0
            }}
          </p>
        </div>
        <div class="flex justify-between items-center mb-3">
          <p class="text-xs lg:text-sm font-thin">
            Our fees {{ transferDetails.percentageFees || ''
            }}{{ transferDetails.percentageFees ? '%' : '' }}
          </p>
          <p class="text-xs lg:text-sm font-thin">
            {{
              fromBalance
                ? parseMoney(transferDetails.fees)
                : parseMoneyAll(transferDetails.fees, country.currency) || 0
            }}
          </p>
        </div>
        <div
          class="flex justify-between items-center mb-3"
          v-if="!fromBalance && transferDetails.partnerCommission"
        >
          <p class="text-xs lg:text-sm font-thin">Partners Commission</p>
          <p class="text-xs lg:text-sm font-thin">
            {{
              parseMoneyAll(transferDetails.partnerCommission, country.currency)
            }}
          </p>
        </div>
        <div v-if="!fromBalance" class="flex justify-between items-center mb-3">
          <p class="text-xs lg:text-sm font-thin">Exchange rate</p>
          <p class="text-xs lg:text-sm font-thin">
            1
            {{ !this.isDivisionOperator ? activeCurrency : secondaryCurrency }}
            to
            {{ exchangeRateBase }}
            {{ !this.isDivisionOperator ? secondaryCurrency : activeCurrency }}
          </p>
        </div>
        <div class="flex justify-between mb-3">
          <p class="text-xs lg:text-sm font-thin">Amount converted</p>
          <p class="text-xs lg:text-sm font-thin">
            {{
              parseMoneyAll(
                transferDetails.debitAmount - transferDetails.fees,
                country.currency
              )
            }}
          </p>
        </div>
        <div
          class="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center mb-3"
        >
          <p class="text-xs font-normal">
            <span class="capitalize">{{
              recipientInfo.recipientName || 'Recipient'
            }}</span>
            gets
          </p>
          <p class="pt-2 lg:pt-0 text-base font-bold max-w-xs lg:max-w-none">
            {{ convertValueTo2DP(transferDetails.recipientAmount || 0) }}
            {{ country2.currency }}
          </p>
        </div>
        <div
          class="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center mb-6"
        >
          <p class="text-xs font-normal">Deposit Method</p>
          <p class="text-xs lg:text-sm font-thin capitalize">
            {{
              !isNewDepositMethod
                ? `${selectedDepositMethod} Holdings`
                : selectedDepositMethod
            }}
          </p>
        </div>
        <hr />
        <div class="flex justify-between items-center mt-8 mb-8">
          <p class="text-xs lg:text-sm font-medium">
            Recipient
            {{
              !transferDestinationIsAsset
                ? isMobileMoneyPayoutMethod
                  ? 'Mobile Money'
                  : 'Bank'
                : 'Wallet'
            }}
            Details
          </p>
          <p
            @click="changeBankDetails"
            class="cursor-pointer text-primary font-normal text-xs lg:text-sm underline"
          >
            Change
          </p>
        </div>
        <div></div>
        <div
          v-for="(info, index) in recipientInformationData.bank_details"
          :key="index"
        >
          <div
            class="flex justify-between items-center mb-3"
            v-if="info.value && !info.disabled"
          >
            <p class="text-xs lg:text-sm font-thin">
              {{ formatServerName(info.key) }}
            </p>
            <p class="text-xs lg:text-sm font-thin capitalize">
              {{ info.value }}
            </p>
          </div>
        </div>
        <div>
          <el-collapse>
            <!-- INTERMEDIARY BANK -->
            <el-collapse-item
              v-if="intermediaryBankDetail"
              title="Intermediary Details"
              name="1"
            >
              <div class="flex justify-end">
                <p
                  class="text-primary text-xs lg:text-sm font-thin underline cursor-pointer"
                  @click="changeRecipientInfoDetails"
                >
                  Change
                </p>
              </div>
              <div
                v-for="(
                  info, index
                ) in recipientInformationData.intermediary_bank_details"
                :key="index"
              >
                <div
                  class="flex justify-between items-center mb-3"
                  v-if="info.value"
                >
                  <p class="text-xs lg:text-sm font-thin">
                    {{ formatServerName(info.key) }}
                  </p>
                  <p class="text-xs lg:text-sm font-thin capitalize">
                    {{ info.value }}
                  </p>
                </div>
              </div>
            </el-collapse-item>
            <!-- RECIPIENT PERSONAL DETAILS -->
            <el-collapse-item
              v-if="hasRecipientDetails"
              title="Recipient Details"
              name="2"
            >
              <div>
                <div class="flex justify-end mb-3">
                  <p
                    class="text-primary text-xs lg:text-sm underline font-normal cursor-pointer"
                    @click="changeRecipientInfoDetails"
                  >
                    Change
                  </p>
                </div>
                <div
                  v-for="(
                    info, index
                  ) in recipientInformationData.recipient_details"
                  :key="index"
                >
                  <div
                    class="flex justify-between items-center mb-3"
                    v-if="info.value"
                  >
                    <p class="text-xs lg:text-sm font-thin">
                      {{ formatServerName(info.key) }}
                    </p>
                    <p class="text-xs lg:text-sm font-thin capitalize">
                      {{ info.value }}
                    </p>
                  </div>
                </div>
              </div>
            </el-collapse-item>
          </el-collapse>
        </div>
      </div>
      <div class="my-8">
        <div class="flex justify-start items-center mt-5 mb-5">
          <p class="text-xs lg:text-sm font-medium">
            Estimated time of arrival
          </p>
        </div>
        <div class="flex justify-between items-center mb-5">
          <p class="text-xs lg:text-sm font-thin">Should arrive</p>
          <p class="text-xs lg:text-sm font-thin">
            {{ transferDestinationIsAsset ? '60 minutes' : 'In 2 days' }}
          </p>
        </div>
      </div>
      <hr />
      <div class="mt-8">
        <form>
          <div class="flex flex-col">
            <label class="text-xs lg:text-sm font-thin">Note (optional)</label>
            <input
              class="h-14 border pl-4 mt-1 bg-root rounded-md"
              placeholder="Enter Reference"
              v-model="note"
            />
          </div>
          <div class="pt-5">
            <el-button
              :loading="isLoading"
              @click="authorize()"
              class="bg-primary w-full text-white font-normal rounded-lg py-5 shadow-lg"
            >
              Continue</el-button
            >
          </div>
        </form>
      </div>
    </div>
    <edit-recipient-details-drawer
      v-if="showRecipientDetailsDrawer"
      :showDrawer="showRecipientDetailsDrawer"
      @closeDrawer="closeDrawer('showRecipientDetailsDrawer')"
      @toReview="next"
    />
    <!-- auth dialog -->
    <verify-auth-dialog
      v-if="showVerifyAuthDialog"
      :authState="authState"
      :showDialog="showVerifyAuthDialog"
      @closeDialog="closeAuthDialog"
      @verifiedUser="verifiedUserTerminalTransfer"
    />
    <transaction-processing
      v-if="showTransferProcessingDialog"
      :showDialog="showTransferProcessingDialog"
      @closeDialog="closeTransferProcessingDialog"
    />
  </div>
</template>

<script>
import {
  ParseMoney,
  ConvertValueToXDP,
  ParseMoneyNaira,
  ParseMoneyLocal,
  ParseMoneyAll,
  ParseNetworkName,
  FormatServerName
} from '@/utils/helpers'
import exchangeRate from '@/mixins/localDeposit.js'
import country from '@/mixins/getCountryDetails.js'
import tenant from '@/mixins/tenant.js'
import terminal from '@/mixins/terminal.js'
export default {
  mixins: [exchangeRate, country, tenant, terminal],
  name: 'ReviewDetails',
  components: {
    BackArrow: () => import('@/assets/icons/BackArrow.vue'),
    VerifyAuthDialog: () => import('@/components/auth/VerifyAuthDialog'),
    EditRecipientDetailsDrawer: () =>
      import(
        '@/components/v2/terminal/drawers/recipientDrawer/EditRecipientDetailsDrawer.vue'
      ),
    TransactionProcessing: () =>
      import('@/components/terminal/TransactionProcessing.vue')
  },
  data () {
    return {
      showRecipientDetailsDrawer: false,
      showVerifyAuthDialog: false,
      showTransferProcessingDialog: false,
      showDepositWalletDialog: false,
      authState: 'transferTerminal',
      isLoading: false,
      note: '',
      walletDetails: {},
      destinationDetails: {},
      initiatedTransferDetails: {}
    }
  },
  methods: {
    closeDrawer (arg) {
      this[arg] = false
    },
    openDrawer (arg) {
      this[arg] = true
    },
    goBack () {
      this.$emit('prev')
    },
    closeDepositWalletDialog () {
      this.showDepositWalletDialog = false
    },
    convertValueTo2DP (val) {
      return ConvertValueToXDP(val, 2)
    },
    parseMoneyAll (val, currency) {
      return ParseMoneyAll(val, currency)
    },
    parseMoney (val) {
      return ParseMoney(val)
    },
    parseMoneyNaira (val) {
      return ParseMoneyNaira(val)
    },
    parseMoneyLocal (val, symbol) {
      return ParseMoneyLocal(val, symbol)
    },
    formatServerName (val) {
      return FormatServerName(val)
    },
    authorize () {
      // if (this.hasBeenDebited || this.transferSourceIsAsset) {
      //   this.next()
      // } else {
      //   this.initiateTransferLocal()
      // }
      this.verifiedUserTerminalTransfer()
    },
    initiateTransferHandler () {
      this.next()
      // this.initiateTransferLocal()
    },
    closeAuthDialog () {
      this.showVerifyAuthDialog = false
    },
    closeTransferProcessingDialog () {
      this.showTransferProcessingDialog = false
    },
    async verifiedUserTerminalTransfer (twoFaCode) {
      await this.getDetails()
      this.isLoading = true
      const {
        accountNo,
        accountName,
        swiftCode,
        recipientName,
        recipientAddress,
        recipientState,
        recipientPostalCode,
        recipientDistrict,
        recipientCity,
        recipientCountry,
        bankName,
        bankCode,
        bankCountry,
        bankBranch,
        bankAccountCountry,
        recipientPhoneNumber,
        recipientAccountCountry
      } = this.recipientInfo
      const selectedBankCountry = this.isFrancDestinationCurrency
        ? bankAccountCountry
        : bankCountry
      const destinationIsGlobalNonUsdDetails = {
        bankDetails: {
          accountNumber: accountNo,
          accountName,
          swiftCode,
          isWithinUs: 'no'
        },
        personalDetails: {
          name: recipientName,
          address: recipientAddress,
          city: recipientCity,
          country: recipientCountry
        }
      }

      let details = {}
      if (!this.transferSourceIsGlobalFiat) {
        details = {
          amount: this.transferDetails.debitAmount,
          destinationCurrency: this.country2.currency,
          note: this.note,
          reason: this.transferDetails.transferReason,
          beneficiaryID: this.usedBeneficiary ? this.beneficiaryID : null,
          destination: this.transferDestinationIsAsset
            ? this.walletDetails
            : this.transferDestinationIsGlobalNonUsdFiat
            ? destinationIsGlobalNonUsdDetails
            : this.destinationDetails
        }
      } else {
        const personalDetails = {
          name: recipientName,
          address: recipientAddress,
          city: recipientCity,
          country: recipientAccountCountry || recipientCountry,
          postalCode: recipientPostalCode,
          state: recipientState,
          district: recipientDistrict,
          phoneNumber: recipientPhoneNumber
        }
        const bankDetails = {
          bankDetails: {
            accountNumber: accountNo,
            bankName,
            bankCode,
            accountName,
            country: selectedBankCountry,
            bankBranch,
            isWithinUs: 'no',
            isMobileMoney: 'no'
          }
        }
        if (this.secondaryCurrencyIsXOF) {
          bankDetails.personalDetails = personalDetails
        }
        const mobileMoneyDetails = {
          bankDetails: {
            accountNumber: accountNo,
            accountName,
            bankName,
            bankCode,
            country: selectedBankCountry,
            isWithinUs: 'no',
            isMobileMoney: 'yes'
          }
        }
        if (this.isFrancDestinationCurrency) {
          mobileMoneyDetails.personalDetails = personalDetails
        }
        details = {
          source: this.$transactionType.FX_TRANSFER,
          note: this.note,
          reason: this.transferDetails.transferReason,
          destinationCurrency: this.country2.currency,
          fxDeposit: {
            amount: this.transferDetails.debitAmount,
            currency: this.country.currency
          },
          beneficiaryID: this.usedBeneficiary ? this.beneficiaryID : null,
          destination: this.transferDestinationIsAsset
            ? this.walletDetails
            : this.isMobileMoneyPayoutMethod
            ? mobileMoneyDetails
            : this.transferDestinationIsGlobalNonUsdFiat
            ? destinationIsGlobalNonUsdDetails
            : bankDetails
        }
      }
      this.setCurrentTransferDetails(details)
    },

    initiateTransferLocal () {
      // this.next()
      this.verifiedUserTerminalTransfer()
    },
    async goToMakePayment () {
      await this.initiateTransferAsset()
    },
    goToEnterAmount () {
      this.$emit('updateAssetTransferDetails')
    },
    setCurrentTransferDetails (details) {
      this.$store.dispatch('terminal/setCurrentTransferDetails', details)
      this.next()
    },
    next () {
      this.$emit('next')
    },
    edit () {
      this.$emit('editTransfer')
      // this.showRecipientDetailsDrawer = true
    },
    changeRecipientInfoDetails () {
      // this.$emit('changeUserDetails')
      this.showRecipientDetailsDrawer = true
    },
    changeBankDetails () {
      this.$store.dispatch('terminal/beneficiaryOption', false)
      this.$store.dispatch('terminal/usedBeneficiary', false)
      this.showRecipientDetailsDrawer = true
      // this.$emit('changeBank')
    },
    parseNetworkName (networkType) {
      return ParseNetworkName(networkType)
    }
  },
  computed: {
    hasBeenDebited () {
      return this.$store.state.terminal.hasBeenDebited
    },
    recipientInformationData () {
      const data = {
        bank_details: [
          {
            key: 'country',
            value: this.isFrancDestinationCurrency
              ? this.recipientInfo?.bankAccountCountry
              : this.recipientInfo?.bankCountry ||
                this.recipientInfo?.recipientCountry ||
                '',
            disabled: this.recipientInfo.network
          },
          {
            key: `${
              this.isMobileMoneyPayoutMethod ? 'phone_number' : 'account_number'
            }`,
            value: this.recipientInfo?.accountNo || ''
          },
          { key: 'swift_code', value: this.recipientInfo?.swiftCode || '' },
          { key: 'account_name', value: this.recipientInfo?.accountName || '' },
          {
            key: `${
              this.isMobileMoneyPayoutMethod ? 'money_provider' : 'bank_name'
            }`,
            value: this.recipientInfo?.bankName || ''
          },
          {
            key: `${
              this.isMobileMoneyPayoutMethod ? 'provider_code' : 'bank_code'
            }`,
            value:
              this.recipientInfo?.bankCode ||
              this.recipientInfo?.providerCode ||
              ''
          },
          { key: 'bank_branch', value: this.recipientInfo?.bankBranch || '' },
          {
            key: 'postal_code',
            value: this.recipientInfo?.bankPostalCode || ''
          },
          { key: 'network', value: this.recipientInfo?.network || '' },
          { key: 'asset', value: this.recipientInfo?.asset || '' },
          {
            key: 'wallet_address',
            value: this.recipientInfo?.walletAddress || ''
          },
          { key: 'reference', value: this.recipientInfo?.reference || '' }
        ],
        intermediary_bank_details: [
          {
            key: 'name',
            value: this.recipientInfo?.intermediaryBankName || ''
          },
          {
            key: 'address',
            value: this.recipientInfo?.intermediaryBankAddress || ''
          },
          {
            key: 'bank_reference',
            value: this.recipientInfo?.intermediaryBankReference || ''
          },
          {
            key: 'swift_code',
            value: this.recipientInfo?.intermediaryBankSwiftCode || ''
          }
        ],
        recipient_details: [
          { key: 'name', value: this.recipientInfo?.recipientName || '' },
          { key: 'address', value: this.recipientInfo?.recipientAddress || '' },
          { key: 'country', value: this.recipientInfo?.recipientCountry || '' },
          { key: 'state', value: this.recipientInfo?.recipientState || '' },
          {
            key: 'city',
            value: this.recipientInfo?.recipientCity || ''
          },
          {
            key: 'postal_code',
            value: this.recipientInfo?.recipientPostalCode || ''
          },
          {
            key: 'district',
            value: this.recipientInfo?.recipientDistrict || ''
          },
          { key: 'email', value: this.recipientInfo?.recipientEmail || '' }
        ]
      }
      return data
    },
    recipientInfo () {
      return (
        this.$store.state.terminal.transferTerminalDetails
          .recipientInformation || {}
      )
    },
    hasRecipientDetails () {
      return (
        this.transferDestinationIsGlobalFiat ||
        (!this.isMobileMoneyPayoutMethod && this.secondaryCurrencyIsXOF)
      )
    },
    transferDetails () {
      return (
        this.$store.state.terminal.transferTerminalDetails.transferDetails || {}
      )
    },
    fromBalance () {
      return this.$store.state.terminal.terminalTransferType === 'fromBalance'
    },
    intermediaryBankDetail () {
      return this.$store.state.terminal.transferTerminalDetails
        .recipientInformation.intermediaryBankName
    },
    isAmericanDetails () {
      return (
        this.recipientInfo.bankCountry === 'US' ||
        !this.recipientInfo.bankCountry
      )
    }
  }
}
</script>

<style scoped>
.card {
  /* box-shadow: 0px 0px 1px rgba(3, 2, 7, 0.1), 0px 4px 8px rgba(3, 2, 7, 0.1); */
  border-radius: 15px;
}
>>> .el-collapse-item__header:hover {
  color: var(--color-primary);
}
>>> .el-collapse-item__header {
  font-size: 14px;
  font-weight: 500;
  padding-top: 15px;
  padding-bottom: 15px;
}
@media screen and (max-width: 800px) {
  >>> .el-collapse-item__header {
    font-size: 13px;
  }
}
</style>
